if (!customElements.get('faq-custom-media')) {
  const DEFAULT_MEDIA_KEY = 'default';
  const SWAP_CLASS = 'is-swapping';
  const DESKTOP_MQ = '(min-width: 750px)';
  const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)';
  const SWAP_DELAY_MS = 120;
  const HOVER_SUPPRESS_MS = 480;

  const templateSelector = (key) =>
    `[data-faq-custom-media-template="${CSS.escape(String(key))}"]`;

  class FaqCustomMedia extends HTMLElement {
    connectedCallback() {
      this.desktopMq = window.matchMedia(DESKTOP_MQ);
      this.reducedMq = window.matchMedia(REDUCED_MOTION_MQ);
      this.mediaTarget = this.querySelector('[data-faq-custom-media-target]');
      this.listEl = this.querySelector('[data-faq-custom-list]');
      this.defaultTemplate = this.querySelector('[data-faq-custom-media-template="default"]');
      this.persistentMediaKey = DEFAULT_MEDIA_KEY;
      this.currentRenderedMediaKey = null;
      this.swapTimeout = null;
      this.hoverPreviewSuppressedUntil = 0;
      this.hoverSummaryEl = null;

      this.templateKeys = new Set(
        Array.from(this.querySelectorAll('[data-faq-custom-media-template]'))
          .map((el) => el.getAttribute('data-faq-custom-media-template'))
          .filter(Boolean)
      );

      this.questions = Array.from(this.querySelectorAll('[data-faq-custom-question]'))
        .map((item) => {
          const details = item.querySelector('details');
          const summary = details?.querySelector('summary');
          return {
            item,
            details,
            summary,
            mediaKey: item.dataset.faqCustomMediaKey || DEFAULT_MEDIA_KEY,
          };
        })
        .filter((q) => q.details && q.summary);

      this.questionByElement = new Map(this.questions.map((q) => [q.item, q]));
      this.questionByDetails = new Map(this.questions.map((q) => [q.details, q]));

      this.onListClick = this.onListClick.bind(this);
      this.onListMouseOver = this.onListMouseOver.bind(this);
      this.onListLeave = this.onListLeave.bind(this);
      this.onListFocusIn = this.onListFocusIn.bind(this);
      this.onListToggle = this.onListToggle.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      if (this.listEl) {
        this.listEl.addEventListener('click', this.onListClick);
        this.listEl.addEventListener('mouseover', this.onListMouseOver);
        this.listEl.addEventListener('mouseout', this.onListLeave);
        this.listEl.addEventListener('focusin', this.onListFocusIn);
        this.listEl.addEventListener('focusout', this.onListLeave);
        this.listEl.addEventListener('toggle', this.onListToggle, true);
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.syncPersistentMediaWithOpenAccordion();
    }

    disconnectedCallback() {
      if (this.listEl) {
        this.listEl.removeEventListener('click', this.onListClick);
        this.listEl.removeEventListener('mouseover', this.onListMouseOver);
        this.listEl.removeEventListener('mouseout', this.onListLeave);
        this.listEl.removeEventListener('focusin', this.onListFocusIn);
        this.listEl.removeEventListener('focusout', this.onListLeave);
        this.listEl.removeEventListener('toggle', this.onListToggle, true);
      }
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.clearSwapTimeout();
    }

    onListClick(event) {
      const summary = event.target.closest?.('summary');
      if (!summary || !this.listEl.contains(summary)) return;
      const entry = this.entryFromSummary(summary);
      if (!entry) return;
      if (this.desktopMq.matches) {
        this.hoverPreviewSuppressedUntil = Date.now() + HOVER_SUPPRESS_MS;
      }
      this.swapMedia(entry.mediaKey);
    }

    onListMouseOver(event) {
      if (!this.desktopMq.matches || Date.now() < this.hoverPreviewSuppressedUntil) return;
      const summary = event.target.closest?.('summary');
      if (!summary || !this.listEl.contains(summary)) return;
      if (summary === this.hoverSummaryEl) return;
      this.hoverSummaryEl = summary;
      this.tryPreviewHoverEntry(this.entryFromSummary(summary));
    }

    onListLeave(event) {
      if (!this.desktopMq.matches) return;
      const r = event.relatedTarget;
      if (r && this.listEl.contains(r)) return;
      this.hoverSummaryEl = null;
      this.syncPersistentMediaWithOpenAccordion();
    }

    onListFocusIn(event) {
      if (!this.desktopMq.matches) return;
      const summary = event.target.closest?.('summary');
      if (!summary || !this.listEl.contains(summary)) return;
      this.tryPreviewHoverEntry(this.entryFromSummary(summary));
    }

    tryPreviewHoverEntry(entry) {
      if (!entry || entry.mediaKey === DEFAULT_MEDIA_KEY || !this.templateKeys.has(entry.mediaKey)) {
        return;
      }
      this.swapMedia(entry.mediaKey);
    }

    onListToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !this.questionByDetails.has(details)) {
        return;
      }
      const active = this.questionByDetails.get(details);
      if (active.details.open) {
        for (const q of this.questions) {
          if (q.details === active.details || !q.details.open) continue;
          const host = q.details.closest('accordion-disclosure');
          host?.classList.remove('is-open');
          q.details.style.height = '';
          q.details.style.overflow = '';
          const content = q.details.querySelector('.accordion__content');
          if (content) {
            content.style.opacity = '';
            content.style.transform = '';
          }
          q.details.open = false;
        }
      }
      this.syncPersistentMediaWithOpenAccordion();
    }

    onBlockSelect(event) {
      const el = event.target.closest('[data-faq-custom-question]');
      if (!el || !this.contains(el)) return;
      const entry = this.questionByElement.get(el);
      if (!entry) return;
      this.persistentMediaKey = entry.mediaKey;
      this.swapMedia(this.persistentMediaKey);
    }

    entryFromSummary(summary) {
      const wrap = summary.closest('[data-faq-custom-question]');
      return wrap ? this.questionByElement.get(wrap) || null : null;
    }

    syncPersistentMediaWithOpenAccordion() {
      const open = this.questions.find((q) => q.details.open);
      this.persistentMediaKey = open ? open.mediaKey : DEFAULT_MEDIA_KEY;
      this.swapMedia(this.persistentMediaKey);
    }

    swapMedia(mediaKey) {
      this.clearSwapTimeout();
      if (!this.mediaTarget) return;

      const requested = this.querySelector(templateSelector(mediaKey));
      const resolvedKey = requested ? mediaKey : DEFAULT_MEDIA_KEY;
      const template = requested || this.defaultTemplate;

      if (!template || resolvedKey === this.currentRenderedMediaKey) return;

      const apply = () => {
        this.mediaTarget.innerHTML = template.innerHTML;
        this.currentRenderedMediaKey = resolvedKey;
      };

      if (!this.currentRenderedMediaKey || this.reducedMq.matches) {
        apply();
        return;
      }

      this.mediaTarget.classList.add(SWAP_CLASS);
      this.swapTimeout = window.setTimeout(() => {
        apply();
        this.mediaTarget.classList.remove(SWAP_CLASS);
        this.swapTimeout = null;
      }, SWAP_DELAY_MS);
    }

    clearSwapTimeout() {
      if (!this.swapTimeout) return;
      window.clearTimeout(this.swapTimeout);
      this.swapTimeout = null;
    }
  }

  customElements.define('faq-custom-media', FaqCustomMedia);
}
